import { useState } from "react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import { JobDetails } from "../components/manual-summarization/JobDetails";
import { JobHistoryList } from "../components/manual-summarization/JobHistoryList";
import { useManualJobHistory } from "../hooks/useManualJobHistory";
import {
  patientIngestErrorMessage,
  validatePatientCsv,
} from "../utils/patientCsvUpload";
import type {
  JobAcceptedResponse,
  RunSummarizationRequest,
} from "../api/clinicalTrialsApiTypes";

type Mode = "mrns" | "csv";

/**
 * Split a free-text MRN blob (comma / newline / space separated) into a clean list.
 *
 * @param text - Raw textarea contents
 * @returns Trimmed, de-duplicated, non-empty MRNs
 */
function parseMrns(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\s,]+/)) {
    const m = raw.trim();
    if (m && !seen.has(m)) {
      seen.add(m);
      out.push(m);
    }
  }
  return out;
}

/**
 * Manual Summarization control panel. Lets an operator drive the scoped
 * ingest+summarize pipeline either from a pasted/typed MRN list or an uploaded
 * OncoEMR "DocumentsCreated" CSV (MRNs are parsed server-side from the
 * `sPatientNumber` column and the raw file is archived for audit). Triggering is
 * asynchronous: after archiving and validating the file the API returns a run id the operator can
 * track (status / processed count) from the same screen.
 *
 * @returns The manual summarization page
 */
export function ManualSummarizationPage() {
  const [mode, setMode] = useState<Mode>("mrns");
  const [mrnText, setMrnText] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [date, setDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const history = useManualJobHistory();

  const mrnCount = parseMrns(mrnText).length;

  const handleFile = (file: File | undefined) => {
    setCsvFile(null);
    if (!file) return;
    const error = validatePatientCsv(file);
    if (error) {
      toast.error(error, "CSV error");
      return;
    }
    setCsvFile(file);
  };

  const handleRun = async () => {
    const body: RunSummarizationRequest = {};
    if (date) body.date = date;
    if (mode === "mrns") {
      const mrns = parseMrns(mrnText);
      if (!mrns.length) {
        toast.error("Enter at least one MRN", "Nothing to run");
        return;
      }
      body.mrns = mrns;
    } else {
      if (!csvFile) {
        toast.error("Choose a CSV file first", "Nothing to run");
        return;
      }
    }
    setSubmitting(true);
    try {
      const res: JobAcceptedResponse =
        mode === "csv" && csvFile
          ? await clinicalTrialsService.uploadPatientCsv(csvFile, {
              csv_format: "documents_created",
              ...(date && { date }),
            })
          : await clinicalTrialsService.runSummarization(body);
      void history.trackNewRun(res.run_id);
      toast.success(
        res.detail || `Run ${res.run_id} accepted`,
        "Summarization started",
      );
    } catch (err) {
      toast.error(patientIngestErrorMessage(err), "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h1 className="text-2xl font-bold mb-1">Manual Summarization</h1>
      <p className="text-sm text-gray-500 mb-6">
        Drive the summarization stack for a specific patient set — paste MRNs or
        upload an OncoEMR DocumentsCreated CSV. The run starts in the background
        and returns a run id you can track below.
      </p>

      <div className="rounded-lg border border-gray-200 p-6 mb-6 bg-white">
        <div className="flex gap-2 mb-4">
          {(["mrns", "csv"] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className="px-4 py-2 rounded-md text-sm font-medium transition-colors"
              style={{
                backgroundColor:
                  mode === m ? "var(--color-primary)" : "transparent",
                color: mode === m ? "#fff" : "var(--color-foreground)",
                border: "1px solid var(--color-primary)",
                cursor: "pointer",
              }}
            >
              {m === "mrns" ? "Enter MRNs" : "Upload CSV"}
            </button>
          ))}
        </div>

        {mode === "mrns" ? (
          <div>
            <label
              htmlFor="manual-mrns"
              className="block text-sm font-medium mb-1"
            >
              MRNs
            </label>
            <textarea
              id="manual-mrns"
              value={mrnText}
              onChange={(e) => setMrnText(e.target.value)}
              rows={5}
              placeholder="Paste MRNs separated by commas, spaces, or new lines"
              className="w-full border border-gray-300 rounded-md p-3 text-sm font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              {mrnCount} MRN(s) detected
            </p>
          </div>
        ) : (
          <div>
            <label
              htmlFor="documents-created-csv"
              className="block text-sm font-medium mb-1"
            >
              DocumentsCreated CSV
            </label>
            <input
              id="documents-created-csv"
              type="file"
              disabled={submitting}
              accept=".csv,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="block text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              CSV files up to 20 MiB.
            </p>
            {csvFile && (
              <p className="text-xs text-gray-500 mt-1">
                Selected <span className="font-medium">{csvFile.name}</span> (
                {(csvFile.size / 1024 / 1024).toFixed(2)} MiB) — MRNs will be
                parsed from the
                <code> sPatientNumber</code> column on the server.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-6 mt-4">
          <div>
            <label
              htmlFor="rollup-date"
              className="block text-sm font-medium mb-1"
            >
              Roll-up date (optional)
            </label>
            <input
              id="rollup-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-md p-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              The day these patients appear under on the dashboard. Defaults to
              yesterday.
            </p>
          </div>
        </div>

        <button
          onClick={handleRun}
          disabled={submitting}
          className="mt-6 px-5 py-2.5 rounded-md text-sm font-semibold text-white transition-opacity"
          style={{
            backgroundColor: "var(--color-primary)",
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Starting…" : "Run Summarization Stack"}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]">
        <JobHistoryList
          jobs={history.jobs}
          selectedRunId={history.selectedJob?.run_id}
          loading={history.loading}
          loadingMore={history.loadingMore}
          error={history.error}
          hasMore={history.hasMore}
          totalCount={history.totalCount}
          onSelect={(runId) => void history.selectRun(runId)}
          onRetry={() => void history.refresh()}
          onLoadMore={() => void history.loadMore()}
        />
        <JobDetails
          job={history.selectedJob}
          loading={history.detailLoading}
          error={history.detailError}
          resuming={history.resumingRunId === history.selectedJob?.run_id}
          onResume={(runId) => {
            void history.resumeRun(runId).then((resumed) => {
              if (resumed) {
                toast.success(
                  "Only unfinished MRNs were queued",
                  "Run resumed",
                );
              }
            });
          }}
        />
      </div>
    </div>
  );
}
